import React, { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import Search from "@mui/icons-material/Search";
import FilterList from "@mui/icons-material/FilterList";
import Refresh from "@mui/icons-material/Refresh";
import Download from "@mui/icons-material/Download";
import History from "@mui/icons-material/History";
import ArrowForward from "@mui/icons-material/ArrowForward";
import ManageSearch from "@mui/icons-material/ManageSearch";
import * as auditLogServices from "../../../contexts/services/admin/auditLogServices";
import LogDetailsDrawer, { StatusChip } from "./LogDetailsDrawer";
import SavedLogViews from "./SavedLogViews";
import {
  actorLabel,
  buildSearchQuery,
  CATEGORIES,
  defaultFilters,
  downloadFile,
  FILTER_LABELS,
  formatTime,
  humanize,
  localTimezone,
  logsToCsv,
  sameFilters,
  TIME_PRESETS,
  validateFilters,
} from "./auditLogUtils";

const cardSx = {
  border: 1,
  borderColor: "divider",
  borderRadius: 3,
  overflow: "hidden",
  boxShadow: "0 3px 16px rgba(30, 20, 50, 0.025)",
};
const advancedFields = [
  "actor_id",
  "target_id",
  "target_type",
  "request_id",
  "ip_address",
];

export default function LogPanel() {
  const initial = useRef(defaultFilters());
  const [draft, setDraft] = useState(initial.current);
  // Only a successful response changes this snapshot: rows, filters and paging always agree.
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validation, setValidation] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [notice, setNotice] = useState(null);
  const sequence = useRef(0);
  const lastAttempt = useRef(null);
  const applied = snapshot?.filters || initial.current;
  const dirty = !sameFilters(draft, applied);
  const result = snapshot?.data;
  const logs = result?.logs || [];
  const notify = (message, severity = "success") =>
    setNotice({ message, severity });

  const fetchLogs = useCallback(async (filters, fixedQuery) => {
    const query = fixedQuery || buildSearchQuery(filters);
    const request = ++sequence.current;
    lastAttempt.current = { filters, query };
    setLoading(true);
    setError("");
    try {
      const response = await auditLogServices.searchAuditLogs(query);
      if (request !== sequence.current) return;
      const data = response.data;
      if (
        !Array.isArray(data?.logs) ||
        !Number.isInteger(data.total) ||
        data.total < 0 ||
        !Number.isInteger(data.page) ||
        data.page < 1 ||
        !Number.isInteger(data.limit) ||
        data.limit < 1
      )
        throw new Error("Invalid audit log response");
      // A cleanup job may remove the last page between requests.
      if (data.total > 0 && data.page > Math.ceil(data.total / data.limit)) {
        const page = Math.ceil(data.total / data.limit);
        await fetchLogs({ ...filters, page }, { ...query, page });
        return;
      }
      const page = data.total ? data.page : 1;
      setSnapshot({
        data: { ...data, page },
        filters: { ...filters, page, limit: data.limit },
        query: { ...query, page, limit: data.limit },
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      if (request !== sequence.current) return;
      setError("Unable to load audit logs. Please retry.");
    } finally {
      if (request === sequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchLogs(initial.current);
    return () => {
      sequence.current += 1;
    };
  }, [fetchLogs]);

  const edit = (key, value) => {
    setValidation("");
    setDraft((previous) => ({ ...previous, [key]: value }));
  };
  const apply = (filters) => {
    const next = { ...filters, page: 1 };
    Object.keys(FILTER_LABELS).forEach((key) => {
      next[key] = next[key].trim();
    });
    const message = validateFilters(next);
    setValidation(message);
    if (message) return;
    setDraft(next);
    void fetchLogs(next);
  };
  const changePage = (page, limit = result.limit) => {
    setDraft((previous) => ({ ...previous, limit }));
    // Pagination retains the exact time bounds of the displayed query, including rolling presets.
    void fetchLogs(
      { ...applied, page, limit },
      { ...snapshot.query, page, limit },
    );
  };
  const retry = () => {
    const attempt = lastAttempt.current;
    if (attempt) void fetchLogs(attempt.filters, attempt.query);
  };
  const track = (filters) => {
    setSelectedLog(null);
    apply({ ...defaultFilters(), period: "all", ...filters });
  };
  const exportPage = () => {
    downloadFile(
      `audit-logs-page-${result.page}-${dayjs(snapshot.updatedAt).format("YYYYMMDD-HHmmss")}.csv`,
      logsToCsv(logs),
      "text/csv;charset=utf-8;",
    );
    notify(`Exported ${logs.length} logs from page ${result.page}.`);
  };
  const advancedCount = ["category", ...advancedFields].filter(
    (key) => draft[key],
  ).length;
  const activeFilters = Object.entries(FILTER_LABELS).filter(
    ([key]) => applied[key],
  );
  const rangeLabel =
    applied.period === "custom"
      ? `${formatTime(snapshot?.query.from, "DD MMM HH:mm")} – ${formatTime(snapshot?.query.to, "DD MMM HH:mm")}`
      : TIME_PRESETS[applied.period];

  return (
    <Box
      sx={{
        p: { xs: 2, lg: 3 },
        minWidth: 0,
        "& .MuiButton-root": { textTransform: "none" },
      }}
    >
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          gap={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              variant="rounded"
              sx={{
                width: 46,
                height: 46,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                borderRadius: 3,
              }}
            >
              <History />
            </Avatar>
            <Box>
              <Typography component="h1" variant="h5" fontWeight={750}>
                Audit Logs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Explore activity, inspect changes and trace related events.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              disabled={loading || !snapshot}
              onClick={() => void fetchLogs({ ...applied, page: 1 })}
            >
              Refresh
            </Button>
            <SavedLogViews
              filters={applied}
              onApply={apply}
              notify={notify}
              disabled={!snapshot || loading}
            />
            <Button
              variant="outlined"
              startIcon={<Download />}
              disabled={!logs.length || loading}
              onClick={exportPage}
            >
              Export page CSV
            </Button>
          </Stack>
        </Stack>

        <Paper
          component="form"
          elevation={0}
          sx={{ ...cardSx, p: 2.5 }}
          onSubmit={(event) => {
            event.preventDefault();
            apply(draft);
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={1.5}
              flexWrap="wrap"
            >
              <TextField
                select
                size="small"
                label="Time range"
                value={draft.period}
                onChange={(event) => {
                  const period = event.target.value;
                  setValidation("");
                  setDraft((previous) => ({
                    ...previous,
                    period,
                    from:
                      period === "custom"
                        ? previous.from ||
                          dayjs().subtract(1, "day").format("YYYY-MM-DDTHH:mm")
                        : "",
                    to:
                      period === "custom"
                        ? previous.to || dayjs().format("YYYY-MM-DDTHH:mm")
                        : "",
                  }));
                }}
                sx={{ minWidth: 170 }}
              >
                {Object.entries(TIME_PRESETS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Actor email"
                value={draft.actor_email}
                onChange={(event) => edit("actor_email", event.target.value)}
                placeholder="Search by email"
                sx={{ flex: 1, minWidth: { sm: 210 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                size="small"
                label="Action keyword"
                value={draft.action}
                onChange={(event) => edit("action", event.target.value)}
                placeholder="e.g. LOGIN or COMPANY"
                sx={{ flex: 1, minWidth: { sm: 200 } }}
              />
              <Stack direction="row" gap={1}>
                <Button
                  variant={advancedOpen ? "contained" : "outlined"}
                  color={advancedOpen ? "primary" : "inherit"}
                  startIcon={<FilterList />}
                  aria-expanded={advancedOpen}
                  aria-controls="audit-advanced-filters"
                  onClick={() => setAdvancedOpen((value) => !value)}
                >
                  Filters{advancedCount ? ` (${advancedCount})` : ""}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  startIcon={<Search />}
                >
                  Search
                </Button>
              </Stack>
            </Stack>
            {draft.period === "custom" && (
              <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
                <TextField
                  size="small"
                  type="datetime-local"
                  label="From"
                  value={draft.from}
                  onChange={(event) => edit("from", event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  type="datetime-local"
                  label="To"
                  value={draft.to}
                  onChange={(event) => edit("to", event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ alignSelf: "center" }}
                >
                  Times in {localTimezone}
                </Typography>
              </Stack>
            )}
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mr: 0.5 }}
              >
                Status
              </Typography>
              {["", "FAILURE", "WARNING", "SUCCESS"].map((status) => (
                <Chip
                  key={status || "all"}
                  label={status ? humanize(status) : "All events"}
                  size="small"
                  clickable
                  aria-pressed={draft.status === status}
                  color={draft.status === status ? "primary" : "default"}
                  variant={draft.status === status ? "filled" : "outlined"}
                  onClick={() => edit("status", status)}
                />
              ))}
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={() => apply(defaultFilters())}>
                Reset filters
              </Button>
            </Stack>
            <Collapse in={advancedOpen} id="audit-advanced-filters">
              <Grid container spacing={2} sx={{ pt: 1 }}>
                <Grid item xs={12} sm={6} lg={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Category"
                    value={draft.category}
                    onChange={(event) => edit("category", event.target.value)}
                  >
                    <MenuItem value="">All categories</MenuItem>
                    {CATEGORIES.map((category) => (
                      <MenuItem key={category} value={category}>
                        {humanize(category)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {advancedFields.map((key) => (
                  <Grid item xs={12} sm={6} lg={4} key={key}>
                    <TextField
                      fullWidth
                      size="small"
                      label={FILTER_LABELS[key]}
                      value={draft[key]}
                      onChange={(event) => edit(key, event.target.value)}
                      placeholder={
                        key === "target_type"
                          ? "e.g. company, subscription"
                          : "Exact match"
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Collapse>
            {dirty && (
              <Typography variant="caption" color="primary.main" role="status">
                Filters have changed. Select Search to apply them; the results
                below still use the displayed query.
              </Typography>
            )}
            {validation && <Alert severity="warning">{validation}</Alert>}
          </Stack>
        </Paper>

        <Paper elevation={0} sx={cardSx}>
          <Box sx={{ height: 3 }}>
            {loading && (
              <LinearProgress
                aria-label="Loading audit logs"
                sx={{ height: 3 }}
              />
            )}
          </Box>
          <Box sx={{ p: 2.5 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              flexWrap="wrap"
              gap={1}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="h6" fontWeight={700}>
                  Activity
                </Typography>
                {snapshot && (
                  <Chip
                    size="small"
                    label={`${result.total.toLocaleString()} events`}
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                aria-live="polite"
              >
                {loading
                  ? snapshot
                    ? "Updating… Showing previous results."
                    : "Loading activity…"
                  : snapshot
                    ? `Updated ${formatTime(snapshot.updatedAt, "HH:mm:ss")} · ${localTimezone}`
                    : "No results loaded"}
              </Typography>
            </Stack>
            {snapshot && (
              <Stack
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
                sx={{ mt: 1.5 }}
              >
                <Typography variant="caption" color="text.secondary">
                  Displayed query
                </Typography>
                <Tooltip
                  title={
                    snapshot.query.from
                      ? `${formatTime(snapshot.query.from)} – ${formatTime(snapshot.query.to)} (${localTimezone})`
                      : "No time restriction"
                  }
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={rangeLabel}
                    onDelete={
                      applied.period !== "all"
                        ? () =>
                            apply({
                              ...applied,
                              period: "all",
                              from: "",
                              to: "",
                            })
                        : undefined
                    }
                  />
                </Tooltip>
                {activeFilters.map(([key, label]) => (
                  <Chip
                    key={key}
                    size="small"
                    variant="outlined"
                    label={`${label}: ${applied[key]}`}
                    onDelete={() => apply({ ...applied, [key]: "" })}
                    sx={{ maxWidth: "100%" }}
                  />
                ))}
              </Stack>
            )}
          </Box>
          {error && (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={retry}
                  disabled={loading}
                >
                  Retry
                </Button>
              }
              sx={{ mx: 2.5, mb: 2 }}
            >
              {error}
              {snapshot
                ? " The previous results and their filters are still shown below."
                : " No results have been loaded."}
            </Alert>
          )}
          {snapshot && (
            <TablePagination
              component="div"
              count={result.total}
              page={result.total ? result.page - 1 : 0}
              rowsPerPage={result.limit}
              rowsPerPageOptions={[20, 50, 100]}
              onPageChange={(_, page) => changePage(page + 1)}
              onRowsPerPageChange={(event) =>
                changePage(1, Number(event.target.value))
              }
              SelectProps={{ disabled: loading }}
              backIconButtonProps={{ disabled: loading || result.page <= 1 }}
              nextIconButtonProps={{
                disabled: loading || result.page * result.limit >= result.total,
              }}
              sx={{
                borderTop: 1,
                borderBottom: 1,
                borderColor: "divider",
                "& .MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  px: { xs: 1, sm: 2 },
                },
                "& .MuiTablePagination-spacer": {
                  display: { xs: "none", sm: "block" },
                },
                "& .MuiTablePagination-actions": { ml: { xs: 0, sm: 2 } },
              }}
            />
          )}
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              aria-label="Audit logs"
              aria-busy={loading}
              sx={{
                minWidth: 850,
                tableLayout: "fixed",
                "& th": {
                  py: 1.5,
                  bgcolor: "action.hover",
                  color: "text.secondary",
                  fontSize: 12,
                  fontWeight: 600,
                },
                "& td": { py: 1.7, borderColor: "divider" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 130 }}>Time</TableCell>
                  <TableCell sx={{ width: "23%" }}>Actor</TableCell>
                  <TableCell sx={{ width: "30%" }}>Action / target</TableCell>
                  <TableCell sx={{ width: 118 }}>Status</TableCell>
                  <TableCell sx={{ width: 145 }}>IP address</TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>
                    Details
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && !snapshot
                  ? Array.from({ length: 6 }, (_, index) => (
                      <TableRow key={index}>
                        {Array.from({ length: 6 }, (_, column) => (
                          <TableCell key={column}>
                            <Skeleton width={column === 2 ? "90%" : "75%"} />
                            <Skeleton width="50%" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : logs.map((log) => {
                      const target = log.details?.target;
                      const targetId = log.target_id || target?.id;
                      return (
                        <TableRow
                          key={log.id}
                          hover
                          onClick={() => setSelectedLog(log)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.035),
                            },
                            "&:focus-within": { bgcolor: "action.hover" },
                          }}
                        >
                          <TableCell>
                            <Tooltip
                              title={`${formatTime(log.created_at)} · ${localTimezone}`}
                            >
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontVariantNumeric: "tabular-nums",
                                    fontWeight: 600,
                                  }}
                                >
                                  {formatTime(log.created_at, "HH:mm:ss")}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {formatTime(log.created_at, "DD MMM YYYY")}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Avatar
                                sx={{
                                  width: 30,
                                  height: 30,
                                  fontSize: 12,
                                  bgcolor: (theme) =>
                                    alpha(theme.palette.primary.main, 0.08),
                                  color: "primary.main",
                                }}
                              >
                                {actorLabel(log).charAt(0).toUpperCase()}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Tooltip title={actorLabel(log)}>
                                  <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    noWrap
                                  >
                                    {actorLabel(log)}
                                  </Typography>
                                </Tooltip>
                                <Tooltip
                                  title={
                                    log.actor_email ||
                                    log.actor?.email ||
                                    "No email recorded"
                                  }
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    component="div"
                                  >
                                    {log.actor_email ||
                                      log.actor?.email ||
                                      humanize(log.details?.actor_type)}
                                  </Typography>
                                </Tooltip>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={log.action || "Unknown action"}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                              >
                                {humanize(log.action)}
                              </Typography>
                            </Tooltip>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              sx={{ mt: 0.5, minWidth: 0 }}
                            >
                              <Typography
                                variant="caption"
                                color="primary.main"
                                sx={{ flexShrink: 0 }}
                              >
                                {humanize(log.category)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.disabled"
                              >
                                ·
                              </Typography>
                              <Tooltip
                                title={
                                  [
                                    target?.label,
                                    log.target_type || target?.type,
                                    targetId,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ") || "No target recorded"
                                }
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                >
                                  {target?.label ||
                                    (targetId
                                      ? `${humanize(log.target_type || target?.type)} · ${String(targetId).slice(0, 10)}…`
                                      : "No target")}
                                </Typography>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={log.status} />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={log.ip_address || "Not recorded"}>
                              <Typography
                                variant="caption"
                                noWrap
                                component="div"
                                sx={{
                                  fontFamily: "monospace",
                                  color: "text.secondary",
                                }}
                              >
                                {log.ip_address || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              endIcon={
                                <ArrowForward
                                  sx={{ fontSize: "14px !important" }}
                                />
                              }
                              aria-label={`View details for ${log.action || "event"} ${log.id}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedLog(log);
                              }}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                {!loading && !logs.length && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ borderBottom: 0 }}>
                      <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
                        <ManageSearch
                          sx={{ fontSize: 44, color: "text.disabled" }}
                        />
                        <Typography variant="subtitle1" fontWeight={600}>
                          {error
                            ? "Activity could not be loaded"
                            : "No matching events"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {error
                            ? "Retry the query to load audit activity."
                            : "Try a wider time range or remove a filter."}
                        </Typography>
                        <Button
                          onClick={
                            error ? retry : () => apply(defaultFilters())
                          }
                        >
                          {error ? "Retry query" : "Reset filters"}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
      {selectedLog && (
        <LogDetailsDrawer
          key={selectedLog.id}
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
          onTrack={track}
          notify={notify}
        />
      )}
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4500}
        onClose={() => setNotice(null)}
      >
        <Alert
          severity={notice?.severity || "success"}
          onClose={() => setNotice(null)}
          variant="filled"
        >
          {notice?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
